import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Header from '../../components/common/Header';
import Button from '../../components/common/Button';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCheck, FiX, FiHelpCircle } = FiIcons;

const PricingPage = () => {
  const [billingPeriod, setBillingPeriod] = useState('yearly');

  const plans = [
    {
      name: 'Free',
      description: 'Perfect for hobbyists and beginners',
      monthlyPrice: 0,
      yearlyPrice: 0,
      storage: '5 GB',
      features: [
        { name: 'Up to 3 galleries', included: true },
        { name: 'Basic photo editing tools', included: true },
        { name: 'Watermarked downloads', included: true },
        { name: 'Public gallery sharing', included: true },
        { name: 'Client proofing', included: false },
        { name: 'Custom branding', included: false },
        { name: 'E-commerce capabilities', included: false },
        { name: 'Booking management', included: false }
      ],
      ctaText: 'Start Free',
      popular: false
    },
    {
      name: 'Pro',
      description: 'For growing photography businesses',
      monthlyPrice: 19.99,
      yearlyPrice: 199.99,
      storage: '100 GB',
      features: [
        { name: 'Unlimited galleries', included: true },
        { name: 'Advanced editing tools', included: true },
        { name: 'Unwatermarked downloads', included: true },
        { name: 'Client proofing & approval', included: true },
        { name: 'Custom branding', included: true },
        { name: 'Basic e-commerce (2% fee)', included: true },
        { name: 'Booking management', included: true },
        { name: 'AI photo organization', included: false }
      ],
      ctaText: 'Start with Pro',
      popular: true
    },
    {
      name: 'Enterprise',
      description: 'For established studios and businesses',
      monthlyPrice: 49.99,
      yearlyPrice: 499.99,
      storage: 'Unlimited',
      features: [
        { name: 'Unlimited galleries', included: true },
        { name: 'All editing tools', included: true },
        { name: 'Unwatermarked downloads', included: true },
        { name: 'Client proofing & approval', included: true },
        { name: 'Custom branding', included: true },
        { name: 'Advanced e-commerce (0% fee)', included: true },
        { name: 'Booking management', included: true },
        { name: 'AI photo organization', included: true }
      ],
      ctaText: 'Contact Sales',
      popular: false
    }
  ];

  const faqs = [
    {
      question: 'Can I upgrade or downgrade my plan?',
      answer: 'Yes, you can upgrade or downgrade your plan at any time. When upgrading, you\'ll be charged the prorated difference immediately. When downgrading, the new rate will apply at your next billing cycle.'
    },
    {
      question: 'Is there a limit to how many clients I can have?',
      answer: 'No, there is no limit to the number of clients you can have on any paid plan. The Free plan has a limit of 10 clients.'
    },
    {
      question: 'How does the e-commerce fee work?',
      answer: 'Pro plan members pay a 2% transaction fee on all sales. Enterprise members pay no transaction fee. Payment processing fees from Stripe or PayPal still apply (typically 2.9% + $0.30 per transaction).'
    },
    {
      question: 'Do you offer a discount for non-profit organizations?',
      answer: 'Yes, we offer special pricing for non-profit organizations. Please contact our sales team for more information.'
    },
    {
      question: 'Can I try the Pro or Enterprise features before committing?',
      answer: 'Yes, we offer a 14-day free trial of the Pro plan with all features enabled. No credit card required to start your trial.'
    },
    {
      question: 'What happens to my data if I cancel my subscription?',
      answer: 'If you downgrade to a Free account, you\'ll maintain access to your data but some features will be disabled. If you cancel completely, you\'ll have 30 days to download your data before it\'s permanently deleted.'
    }
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-dark-900">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-50 to-secondary-50 dark:from-primary-900/10 dark:to-secondary-900/10" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6"
          >
            Simple, Transparent Pricing
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto mb-8"
          >
            Choose the plan that's right for your photography business, with no hidden fees or surprises.
          </motion.p>
          
          {/* Billing Toggle */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="flex items-center justify-center mb-12"
          >
            <span className={`text-sm ${billingPeriod === 'monthly' ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}`}>
              Monthly
            </span>
            <label className="mx-4 relative inline-flex items-center cursor-pointer">
              <input 
                type="checkbox" 
                className="sr-only peer" 
                checked={billingPeriod === 'yearly'} 
                onChange={() => setBillingPeriod(billingPeriod === 'monthly' ? 'yearly' : 'monthly')}
              />
              <div className="w-14 h-7 bg-gray-200 dark:bg-gray-700 rounded-full peer peer-checked:after:translate-x-7 peer-checked:bg-primary-600 after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all"></div>
            </label>
            <span className={`text-sm ${billingPeriod === 'yearly' ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}`}>
              Yearly <span className="text-green-600 font-medium">Save 15%</span>
            </span>
          </motion.div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="pb-24 -mt-6 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            {plans.map((plan, index) => (
              <motion.div
                key={plan.name}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={`bg-white dark:bg-dark-800 rounded-2xl shadow-lg border ${
                  plan.popular 
                    ? 'border-primary-500 dark:border-primary-500' 
                    : 'border-gray-200 dark:border-gray-700'
                } overflow-hidden relative`}
              >
                {plan.popular && (
                  <div className="bg-primary-500 text-white text-xs font-bold px-3 py-1 uppercase tracking-wider absolute top-0 right-0 rounded-bl-lg">
                    Most Popular
                  </div>
                )}
                <div className="p-8">
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                    {plan.name}
                  </h3>
                  <p className="text-gray-500 dark:text-gray-400 mb-6">
                    {plan.description}
                  </p>
                  <div className="mb-6">
                    <span className="text-4xl font-bold text-gray-900 dark:text-white">
                      ${billingPeriod === 'yearly' ? plan.yearlyPrice : plan.monthlyPrice}
                    </span>
                    {plan.monthlyPrice > 0 && (
                      <span className="text-gray-500 dark:text-gray-400">
                        /{billingPeriod === 'yearly' ? 'year' : 'month'}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                    <strong>{plan.storage}</strong> storage included
                  </p>
                  <Link to={plan.name === 'Enterprise' ? '/contact' : '/signup'}>
                    <Button
                      variant={plan.popular ? 'primary' : 'outline'}
                      className="w-full"
                    >
                      {plan.ctaText}
                    </Button>
                  </Link>
                </div>
                <div className="border-t border-gray-200 dark:border-gray-700 p-8">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-4">
                    What's included:
                  </h4>
                  <ul className="space-y-3">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-start">
                        <div className={`mt-1 w-5 h-5 rounded-full ${
                          feature.included 
                            ? 'bg-green-100 dark:bg-green-900/30' 
                            : 'bg-gray-100 dark:bg-gray-800'
                        } flex items-center justify-center flex-shrink-0`}>
                          <SafeIcon 
                            icon={feature.included ? FiCheck : FiX} 
                            className={`w-3 h-3 ${
                              feature.included 
                                ? 'text-green-600 dark:text-green-400' 
                                : 'text-gray-400 dark:text-gray-500'
                            }`} 
                          />
                        </div>
                        <span className={`ml-3 ${
                          feature.included 
                            ? 'text-gray-600 dark:text-gray-300' 
                            : 'text-gray-400 dark:text-gray-500'
                        }`}>
                          {feature.name}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Comparison */}
      <section className="py-20 bg-gray-50 dark:bg-dark-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
              Compare Plan Features
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Find the plan that's right for your photography business.
            </p>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="py-4 px-6 text-left text-gray-500 dark:text-gray-400 font-medium">Feature</th>
                  <th className="py-4 px-6 text-center text-gray-900 dark:text-white font-bold">Free</th>
                  <th className="py-4 px-6 text-center text-gray-900 dark:text-white font-bold">Pro</th>
                  <th className="py-4 px-6 text-center text-gray-900 dark:text-white font-bold">Enterprise</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <td className="py-4 px-6 text-gray-900 dark:text-white">Storage</td>
                  <td className="py-4 px-6 text-center text-gray-600 dark:text-gray-300">5 GB</td>
                  <td className="py-4 px-6 text-center text-gray-600 dark:text-gray-300">100 GB</td>
                  <td className="py-4 px-6 text-center text-gray-600 dark:text-gray-300">Unlimited</td>
                </tr>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <td className="py-4 px-6 text-gray-900 dark:text-white">Galleries</td>
                  <td className="py-4 px-6 text-center text-gray-600 dark:text-gray-300">3</td>
                  <td className="py-4 px-6 text-center text-gray-600 dark:text-gray-300">Unlimited</td>
                  <td className="py-4 px-6 text-center text-gray-600 dark:text-gray-300">Unlimited</td>
                </tr>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <td className="py-4 px-6 text-gray-900 dark:text-white">Clients</td>
                  <td className="py-4 px-6 text-center text-gray-600 dark:text-gray-300">10</td>
                  <td className="py-4 px-6 text-center text-gray-600 dark:text-gray-300">Unlimited</td>
                  <td className="py-4 px-6 text-center text-gray-600 dark:text-gray-300">Unlimited</td>
                </tr>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <td className="py-4 px-6 text-gray-900 dark:text-white">E-commerce</td>
                  <td className="py-4 px-6 text-center">
                    <SafeIcon icon={FiX} className="w-5 h-5 text-red-500 mx-auto" />
                  </td>
                  <td className="py-4 px-6 text-center text-gray-600 dark:text-gray-300">2% fee</td>
                  <td className="py-4 px-6 text-center text-gray-600 dark:text-gray-300">0% fee</td>
                </tr>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <td className="py-4 px-6 text-gray-900 dark:text-white">Custom Branding</td>
                  <td className="py-4 px-6 text-center">
                    <SafeIcon icon={FiX} className="w-5 h-5 text-red-500 mx-auto" />
                  </td>
                  <td className="py-4 px-6 text-center">
                    <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-500 mx-auto" />
                  </td>
                  <td className="py-4 px-6 text-center">
                    <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-500 mx-auto" />
                  </td>
                </tr>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <td className="py-4 px-6 text-gray-900 dark:text-white">Watermark Removal</td>
                  <td className="py-4 px-6 text-center">
                    <SafeIcon icon={FiX} className="w-5 h-5 text-red-500 mx-auto" />
                  </td>
                  <td className="py-4 px-6 text-center">
                    <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-500 mx-auto" />
                  </td>
                  <td className="py-4 px-6 text-center">
                    <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-500 mx-auto" />
                  </td>
                </tr>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <td className="py-4 px-6 text-gray-900 dark:text-white">AI Photo Organization</td>
                  <td className="py-4 px-6 text-center">
                    <SafeIcon icon={FiX} className="w-5 h-5 text-red-500 mx-auto" />
                  </td>
                  <td className="py-4 px-6 text-center">
                    <SafeIcon icon={FiX} className="w-5 h-5 text-red-500 mx-auto" />
                  </td>
                  <td className="py-4 px-6 text-center">
                    <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-500 mx-auto" />
                  </td>
                </tr>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <td className="py-4 px-6 text-gray-900 dark:text-white">Priority Support</td>
                  <td className="py-4 px-6 text-center">
                    <SafeIcon icon={FiX} className="w-5 h-5 text-red-500 mx-auto" />
                  </td>
                  <td className="py-4 px-6 text-center">
                    <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-500 mx-auto" />
                  </td>
                  <td className="py-4 px-6 text-center">
                    <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-500 mx-auto" />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300">
              Find answers to common questions about our pricing and plans.
            </p>
          </div>
          
          <div className="space-y-8">
            {faqs.map((faq, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white dark:bg-dark-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700"
              >
                <div className="flex items-start">
                  <div className="mr-4 mt-1">
                    <SafeIcon icon={FiHelpCircle} className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                      {faq.question}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary-600 to-secondary-600 text-white">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl opacity-90 mb-8">
            Join thousands of photographers who trust Tasveer for their business.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/signup">
              <Button 
                variant="secondary" 
                size="xl" 
                className="bg-white text-primary-600 hover:bg-gray-100"
              >
                Start Free Trial
              </Button>
            </Link>
            <Link to="/contact">
              <Button 
                variant="outline" 
                size="xl" 
                className="border-white text-white hover:bg-white hover:text-primary-600"
              >
                Contact Sales
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PricingPage;